import { useCallback, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { SlidersHorizontal } from "lucide-react";

import { PageHeader } from "@/components/shared/PageHeader";
import { LoadingLogo } from "@/components/shared/LoadingLogo";
import { DatePickerField } from "@/components/shared/DatePickerField";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { listClients, type Client } from "@/services/clientService";
import { getSalesReport, downloadSalesCsv } from "@/services/reportService";
import { SalesReportCard } from "@/pages/reports/SalesReportCard";

function getMonthRange(offset: number): { from: string; to: string } {
  const now = new Date();
  const d = new Date(now.getFullYear(), now.getMonth() + offset, 1);
  const year = d.getFullYear();
  const month = d.getMonth();
  const lastDay = new Date(year, month + 1, 0).getDate();
  const from = `${year}-${String(month + 1).padStart(2, "0")}-01`;
  const to = `${year}-${String(month + 1).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
  return { from, to };
}

function getMonthName(offset: number, locale: string): string {
  const now = new Date();
  const d = new Date(now.getFullYear(), now.getMonth() + offset, 1);
  const name = d.toLocaleString(locale, { month: "long" });
  return name.charAt(0).toUpperCase() + name.slice(1);
}

type PeriodOption = "month" | "last_month" | "last_3_months" | "custom";

const VALID_PERIODS: PeriodOption[] = ["month", "last_month", "last_3_months", "custom"];

export default function ReportsPage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [csvLoading, setCsvLoading] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const period = (VALID_PERIODS.includes(searchParams.get("period") as PeriodOption)
    ? searchParams.get("period")
    : "month") as PeriodOption;
  const customFrom = searchParams.get("from") ?? "";
  const customTo = searchParams.get("to") ?? "";
  const clientId = searchParams.get("client") ?? "";
  const currency = searchParams.get("currency") ?? "";

  const setParam = useCallback(
    (key: string, value: string) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (value) {
          next.set(key, value);
        } else {
          next.delete(key);
        }
        return next;
      }, { replace: true });
    },
    [setSearchParams],
  );

  const setPeriod = useCallback((v: PeriodOption) => setParam("period", v === "month" ? "" : v), [setParam]);
  const setCustomFrom = useCallback((v: string) => setParam("from", v), [setParam]);
  const setCustomTo = useCallback((v: string) => setParam("to", v), [setParam]);
  const setClientId = useCallback((v: string) => setParam("client", v), [setParam]);
  const setCurrency = useCallback((v: string) => setParam("currency", v), [setParam]);

  const thisMonthName = useMemo(() => getMonthName(0, i18n.language), [i18n.language]);
  const lastMonthName = useMemo(() => getMonthName(-1, i18n.language), [i18n.language]);

  const periodLabel = useMemo(() => {
    if (period === "month") return thisMonthName;
    if (period === "last_month") return lastMonthName;
    if (period === "last_3_months") return t("reports.last3Months", "Últimos 3 meses");
    return t("reports.custom", "Personalizado");
  }, [period, thisMonthName, lastMonthName, t]);

  const { data: clientsData } = useQuery({
    queryKey: ["clients", "list-select"],
    queryFn: () => listClients({ limit: 100 }),
  });
  const clients: Client[] = clientsData?.data ?? [];

  const dateRange = useMemo(() => {
    if (period === "month") return getMonthRange(0);
    if (period === "last_month") return getMonthRange(-1);
    if (period === "last_3_months") {
      return { from: getMonthRange(-2).from, to: getMonthRange(0).to };
    }
    return { from: customFrom, to: customTo };
  }, [period, customFrom, customTo]);

  const canFetch = Boolean(dateRange.from && dateRange.to);

  const { data, isLoading } = useQuery({
    queryKey: ["reports", "sales", dateRange.from, dateRange.to, clientId, currency],
    queryFn: () => getSalesReport(dateRange.from, dateRange.to),
    enabled: canFetch,
  });

  const activeFilterCount = (clientId ? 1 : 0) + (currency ? 1 : 0);

  const clearFilters = useCallback((): void => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("client");
      next.delete("currency");
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Client-side filtering (backend returns all, we filter here for simplicity)
  const filteredData = useMemo(() => {
    if (!data) return data;
    if (!clientId && !currency) return data;

    const filterClient = clientId
      ? (name: string, rut: string | null) => {
          const client = clients.find((c) => c.id === clientId);
          if (!client) return true;
          return (
            name === client.legalName ||
            (rut && client.rut && rut === client.rut)
          );
        }
      : () => true;

    const filterCurrency = currency
      ? (uyu: number, usd: number) => {
          if (currency === "UYU") return uyu !== 0;
          if (currency === "USD") return usd !== 0;
          return true;
        }
      : () => true;

    return {
      ...data,
      byClient: data.byClient.filter(
        (r) => filterClient(r.clientName, r.clientRut) && filterCurrency(r.totalUyu, r.totalUsd)
      ),
    };
  }, [data, clientId, currency, clients]);

  const handleDownloadCsv = async (): Promise<void> => {
    if (!canFetch) return;
    setCsvLoading(true);
    try {
      await downloadSalesCsv(dateRange.from, dateRange.to);
    } catch {
      toast.error(t("reports.sales.downloadError", "No se pudo descargar el reporte."));
    } finally {
      setCsvLoading(false);
    }
  };

  return (
    <div className="fx-content">
      <PageHeader
        eyebrow={t("reports.eyebrow", "Análisis")}
        title={t("reports.title", "Reportes")}
      />

      <div className="mt-4 flex flex-wrap items-end gap-3">
        {/* Period selector */}
        <div className="space-y-1">
          <span className="text-sm text-muted-foreground">
            {t("reports.period", "Período")}
          </span>
          <Select
            value={period}
            onValueChange={(v) => setPeriod(v as PeriodOption)}
          >
            <SelectTrigger className="w-[200px]">
              <SelectValue>{periodLabel}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="month">
                {t("reports.thisMonth", "Este mes")} ({thisMonthName.toLowerCase()})
              </SelectItem>
              <SelectItem value="last_month">
                {t("reports.lastMonth", "Mes anterior")} ({lastMonthName.toLowerCase()})
              </SelectItem>
              <SelectItem value="last_3_months">
                {t("reports.last3Months", "Últimos 3 meses")}
              </SelectItem>
              <SelectItem value="custom">
                {t("reports.custom", "Personalizado")}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        {period === "custom" && (
          <>
            <DatePickerField
              value={customFrom}
              onChange={setCustomFrom}
              label={t("reports.from", "Desde")}
              labelPosition="inline"
            />
            <DatePickerField
              value={customTo}
              onChange={setCustomTo}
              label={t("reports.to", "Hasta")}
              labelPosition="inline"
            />
          </>
        )}

        {/* Filters dialog */}
        <Dialog open={filtersOpen} onOpenChange={setFiltersOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" size="sm" className="h-10 gap-2">
              <SlidersHorizontal className="h-4 w-4 shrink-0" />
              {t("reports.filters", "Filtros")}
              {activeFilterCount > 0 && (
                <Badge variant="secondary" className="shrink-0 tabular-nums">
                  {activeFilterCount}
                </Badge>
              )}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-sm gap-0 overflow-hidden p-0 sm:rounded-xl">
            <DialogHeader className="border-b border-border px-4 py-4 text-left">
              <DialogTitle>{t("reports.filterTitle", "Filtrar reporte")}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 px-4 py-4">
              <div className="space-y-1.5">
                <span className="text-sm text-muted-foreground">
                  {t("reports.filterClient", "Cliente")}
                </span>
                <Select value={clientId || "all"} onValueChange={(v) => setClientId(v === "all" ? "" : v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("reports.allClients", "Todos los clientes")}</SelectItem>
                    {clients.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.displayName?.trim() || c.legalName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <span className="text-sm text-muted-foreground">
                  {t("reports.filterCurrency", "Moneda")}
                </span>
                <Select value={currency || "all"} onValueChange={(v) => setCurrency(v === "all" ? "" : v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("reports.allCurrencies", "Todas")}</SelectItem>
                    <SelectItem value="UYU">UYU</SelectItem>
                    <SelectItem value="USD">USD</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center justify-between border-t border-border bg-muted/30 px-4 py-3">
              {activeFilterCount > 0 ? (
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  {t("reports.clearFilters", "Quitar filtros")}
                </Button>
              ) : (
                <span />
              )}
              <Button size="sm" onClick={() => setFiltersOpen(false)}>
                {t("reports.filtersDone", "Listo")}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {activeFilterCount > 0 && (
          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground"
            onClick={clearFilters}
          >
            {t("reports.clearFilters", "Quitar filtros")}
          </Button>
        )}
      </div>

      <div className="mt-6">
        {isLoading ? (
          <div className="flex min-h-[30vh] items-center justify-center">
            <LoadingLogo size="lg" />
          </div>
        ) : filteredData ? (
          <SalesReportCard
            data={filteredData}
            onDownloadCsv={handleDownloadCsv}
            csvLoading={csvLoading}
          />
        ) : canFetch ? null : (
          <p className="text-sm text-muted-foreground">
            {t("reports.selectPeriod", "Seleccioná un período para ver el reporte.")}
          </p>
        )}
      </div>
    </div>
  );
}
